import { useEffect, useState } from "react";
import { deepmerge } from "@mui/utils";
import { createTheme, ThemeProvider, responsiveFontSizes } from "@mui/material/styles";
import "@fontsource/open-sans/300.css";
import "@fontsource/open-sans/400.css";
import "@fontsource/open-sans/500.css";
import "@fontsource/open-sans/600.css";
import "@fontsource/open-sans/700.css";
import { ICustomerConfiguration } from "@/interfaces/ICustomerConfiguration";
import { ScopedCssBaseline } from "@mui/material";
import { convertHexToRGBA } from "./tools/tools";
import { themeDefault } from "./themes/themeDefault";
import useStore from "@store";
import { fetchInit } from "@api";
import { getCurrentTheme } from "./themes/toolsTheme";
import RouterContent from "./components/RouterContent";
import { BrowserRouter, HashRouter } from "react-router-dom";
import { URL_MODES } from "@/enums/EnumUrl";

const App = () => {
  const { setCustomerConfiguration, setEquipments, setError, setCustomerTheme, customerTheme, customerConfiguration } = useStore();
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [theme, setTheme] = useState(createTheme(themeDefault));

  // Fetching Init Data from API
  useEffect(() => {
    const fetchInitData = async () => {
      try {
        const data: any = await fetchInit();
        setEquipments(data.ausstattungen);
        setThemeConfig(data.config);
        setCustomerConfiguration(data.config);
      } catch (error) {
        setError("Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später nochmal.");
      } finally {
        setIsDataLoaded(true);
      }
    };
    fetchInitData();
  }, [useStore]);

  // Set User theme configuration
  const setThemeConfig = (config: ICustomerConfiguration) => {
    const theme1 = createTheme(config && config.styles.mui ? deepmerge(themeDefault, config.styles.mui || {}) : themeDefault);

    const theme2 = {
      palette: {
        action: {
          active: theme1.palette.grey[500],
          hover: convertHexToRGBA(theme1.palette.primary.main, 0.1),
          selected: convertHexToRGBA(theme1.palette.primary.main, 0.1),
          disabled: theme1.palette.grey[500],
          disabledBackground: theme1.palette.grey[200]
        }
      },
      components: {
        MuiTab: {
          styleOverrides: {
            root: {
              "&.Mui-selected": {
                backgroundColor: theme1.palette.primary.main
              }
            }
          }
        }
      }
    };

    const newTheme = deepmerge(theme1, theme2);
    setCustomerTheme(responsiveFontSizes(createTheme(newTheme)));

    // set html root font size (default: 16px)
    document.documentElement.style.fontSize = config?.styles?.custom?.fontSize || "16px";
  };

  // Change theme for special cases
  useEffect(() => {
    const currentTheme = getCurrentTheme(window.location.pathname, customerConfiguration, customerTheme);
    setTheme(responsiveFontSizes(createTheme(currentTheme)));
  }, [customerTheme]);

  if (!isDataLoaded || !customerConfiguration) {
    return null;
  }

  const urlMode = customerConfiguration?.features?.urlMode;
  const RouterComponent = urlMode === URL_MODES.SEO_BAD ? HashRouter : BrowserRouter;

  return (
    <RouterComponent
      basename={urlMode === URL_MODES.SEO_BAD ? "/" : undefined}
      future={{
        v7_startTransition: false,
        v7_relativeSplatPath: false
      }}
    >
      <ScopedCssBaseline sx={{ background: "transparent" }}>
        <ThemeProvider theme={theme}>
          <RouterContent customerConfig={customerConfiguration} />
        </ThemeProvider>
      </ScopedCssBaseline>
    </RouterComponent>
  );
};

export default App;
